import React, { useState } from 'react';
import { Facebook, Twitter, Linkedin, Link2, Mail, Check } from 'lucide-react';
import { Button } from './ui/button';

/**
 * SocialShare Component
 * Share buttons for product pages
 */
const SocialShare = ({ 
  url, 
  title, 
  description = '',
  showLabel = true,
  size = 'default' // 'small' | 'default'
}) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const shareTitle = encodeURIComponent(title || 'Check out this product!');
  const shareDescription = encodeURIComponent(description);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${shareTitle}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    email: `mailto:?subject=${shareTitle}&body=${shareDescription}%0A%0A${encodeURIComponent(shareUrl)}`
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const openShareWindow = (url) => {
    window.open(url, '_blank', 'width=600,height=400,scrollbars=yes');
  };

  const iconSize = size === 'small' ? 'h-4 w-4' : 'h-5 w-5';
  const buttonSize = size === 'small' ? 'h-8 w-8' : 'h-10 w-10';

  return (
    <div className="flex items-center gap-3" data-testid="social-share">
      {showLabel && (
        <span className="text-sm text-[var(--muted)] font-medium">Share:</span>
      )}
      
      <div className="flex items-center gap-2">
        {/* Facebook */}
        <Button
          variant="outline"
          size="icon"
          className={`${buttonSize} rounded-full hover:bg-blue-50 hover:border-blue-200 hover:text-blue-600`}
          onClick={() => openShareWindow(shareLinks.facebook)}
          title="Share on Facebook"
          data-testid="share-facebook"
        >
          <Facebook className={iconSize} />
        </Button>

        {/* Twitter/X */}
        <Button
          variant="outline"
          size="icon"
          className={`${buttonSize} rounded-full hover:bg-sky-50 hover:border-sky-200 hover:text-sky-600`}
          onClick={() => openShareWindow(shareLinks.twitter)}
          title="Share on Twitter"
          data-testid="share-twitter"
        >
          <Twitter className={iconSize} />
        </Button>

        {/* LinkedIn */}
        <Button
          variant="outline"
          size="icon"
          className={`${buttonSize} rounded-full hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700`}
          onClick={() => openShareWindow(shareLinks.linkedin)}
          title="Share on LinkedIn"
          data-testid="share-linkedin"
        >
          <Linkedin className={iconSize} />
        </Button>

        {/* Email */}
        <Button
          variant="outline"
          size="icon"
          className={`${buttonSize} rounded-full hover:bg-gray-50 hover:border-gray-300`}
          onClick={() => window.location.href = shareLinks.email}
          title="Share via Email"
          data-testid="share-email"
        >
          <Mail className={iconSize} />
        </Button>

        {/* Copy Link */}
        <Button
          variant="outline"
          size="icon"
          className={`${buttonSize} rounded-full ${copied ? 'bg-green-50 border-green-200 text-green-600' : 'hover:bg-gray-50 hover:border-gray-300'}`}
          onClick={handleCopyLink}
          title={copied ? 'Copied!' : 'Copy link'}
          data-testid="share-copy"
        >
          {copied ? <Check className={iconSize} /> : <Link2 className={iconSize} />}
        </Button>
      </div>
    </div>
  );
};

export default SocialShare;
